import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, ShoppingBag, Loader2, Lock } from "lucide-react";
import { useCart } from "@/components/CartProvider";
import { api } from "@/lib/api";
import { toast } from "sonner";

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

export const CartDrawer = () => {
  const { items, remove, total, open, setOpen, count } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout-cart", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty })),
        email: email || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-bsn-parchment w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-bsn-midnight flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-bsn-gold" /> Your Downloads
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-3">
          {items.length === 0 && (
            <p className="text-sm text-bsn-sapphire/70 text-center py-12" data-testid="cart-empty">
              Your cart is quiet for now. Explore the Digital Shop to add printable resources.
            </p>
          )}
          {items.map((i) => (
            <div key={i.lookup_key} className="flex items-start justify-between gap-3 bg-white rounded-xl p-3 card-tactile" data-testid={`cart-item-${i.lookup_key}`}>
              <div>
                <p className="font-serif font-semibold text-bsn-midnight leading-snug">{i.name}</p>
                <p className="text-xs text-bsn-sapphire/70 mt-1">{money(i.amount)} × {i.qty}</p>
              </div>
              <button onClick={() => remove(i.lookup_key)} className="p-1.5 text-bsn-sapphire/60 hover:text-red-600 transition-colors" data-testid={`cart-remove-${i.lookup_key}`} aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-bsn-gold/25 pt-4 space-y-3">
            <div className="flex items-center justify-between font-serif text-lg">
              <span className="text-bsn-sapphire">Total</span>
              <span className="font-semibold text-bsn-midnight" data-testid="cart-total">{money(total)}</span>
            </div>
            <Input
              type="email"
              placeholder="Email for your download links (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white"
              data-testid="cart-email-input"
            />
            <Button onClick={checkout} disabled={loading} className="w-full btn-gold font-semibold h-12 text-base" data-testid="cart-checkout-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Secure Checkout · {money(total)}</>}
            </Button>
            <p className="text-[11px] text-center text-bsn-sapphire/60">
              Powered by Stripe. Test card 4242 4242 4242 4242, any future date & CVC.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
